import React, { useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TextInput, TouchableOpacity, Alert } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RootStackParamList } from '../types/navigation';
import { Icon } from '../components/Icon';
import { colors, spacing, fontWeights, borderRadius } from '../theme';

type NavigationProp = StackNavigationProp<RootStackParamList, 'M11HistoryScreen'>;

interface HistoryItem {
  id: string;
  name: string;
  proposalName: string;
  premium: string;
  lastUpdated: string;
  status: string;
}

const HISTORY_ITEMS: HistoryItem[] = [
  {
    id: '1',
    name: 'Tạ Minh Luận',
    proposalName: 'Sản phẩm Bảo hiểm Liên kết chung Xanh Ước Mơ',
    premium: '15.209',
    lastUpdated: '22/06/2026',
    status: 'EXP: 27/07/2026',
  },
  {
    id: '2',
    name: 'Nguyễn Văn Minh',
    proposalName: 'Sản phẩm Bảo hiểm Liên kết đơn vị Xanh Phú Quý',
    premium: '20.000',
    lastUpdated: '19/06/2026',
    status: 'EXP: 24/07/2026',
  },
  {
    id: '3',
    name: 'Trần Thị B',
    proposalName: 'Sản phẩm Bảo hiểm Liên kết đơn vị Xanh Tương Lai',
    premium: '30.000',
    lastUpdated: '18/06/2026',
    status: 'EXP: 23/07/2026',
  },
  {
    id: '4',
    name: 'Lê Văn C',
    proposalName: 'Sản phẩm Bảo hiểm Liên kết chung Xanh Ước Mơ',
    premium: '12.500',
    lastUpdated: '15/06/2026',
    status: 'EXP: 20/07/2026',
  },
];

export const M11HistoryScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const [search, setSearch] = useState('');

  const filteredHistory = HISTORY_ITEMS.filter((item) =>
    item.name.toLowerCase().includes(search.toLowerCase()) || 
    item.proposalName.toLowerCase().includes(search.toLowerCase())
  );

  const handleCreateNew = () => {
    navigation.navigate('M04ProductListScreen');
  };

  const handleDownloadPDF = (name: string) => {
    Alert.alert('Tải xuống PDF', `Đã tải xuống thành công tài liệu minh họa của khách hàng: ${name}`);
  };

  return (
    <View style={styles.container}>
      {/* Header Controls: Search + Create Button */}
      <View style={styles.headerPanel}>
        <View style={styles.searchWrapper}>
          <Icon name="search" size={20} color={colors.onSurfaceVariant} style={styles.searchIcon} />
          <TextInput
            style={styles.searchInput}
            placeholder="Bên Mua Bảo Hiểm / Tên TLMHBH"
            value={search}
            onChangeText={setSearch}
          />
          {search.length > 0 && (
            <TouchableOpacity onPress={() => setSearch('')}>
              <Icon name="close" size={20} color={colors.onSurfaceVariant} />
            </TouchableOpacity>
          )}
        </View>
        <TouchableOpacity style={styles.createBtn} onPress={handleCreateNew}>
          <Text style={styles.createBtnText}>Tạo TLMHBH</Text>
        </TouchableOpacity>
      </View>

      <ScrollView contentContainerStyle={styles.scrollContent}>
        {/* Warning/Alert Banner */}
        <View style={styles.alertBanner}>
          <Icon name="info" size={20} color="#B45309" style={styles.alertIcon} />
          <Text style={styles.alertText}>
            Tính năng này hỗ trợ tạo nhanh bản nháp Tài liệu Minh họa Bán hàng, và không thể thay thế Tài liệu Minh họa Bán hàng bản đầy đủ trên ePOS. Chỉ trình bày Tài liệu Minh họa Bán hàng khi đã đánh giá kỹ lưỡng về Nhu cầu cũng như Hoàn cảnh của từng Khách hàng.
          </Text>
        </View>

        {/* Scroll helper indicator */}
        <Text style={styles.scrollHelperText}>Vui lòng kéo sang phải để xem đầy đủ thông tin danh sách.</Text>

        {/* Data Grid / Table Wrapper */}
        <ScrollView horizontal showsHorizontalScrollIndicator={true} style={styles.tableScroll}>
          <View style={styles.table}>
            {/* Table Header */}
            <View style={styles.tableHeader}>
              <Text style={[styles.headerCell, { width: 140 }]}>Tên khách hàng</Text>
              <Text style={[styles.headerCell, { width: 280 }]}>Tên Đề xuất</Text>
              <Text style={[styles.headerCell, { width: 100 }]}>Phí bảo hiểm</Text>
              <Text style={[styles.headerCell, { width: 120 }]}>Cập Nhật Lần Cuối</Text>
              <Text style={[styles.headerCell, { width: 130 }]}>Trạng thái</Text>
              <Text style={[styles.headerCell, { width: 60, textAlign: 'center' }]}></Text>
            </View>

            {/* Table Rows */}
            {filteredHistory.map((item, index) => (
              <View 
                key={item.id} 
                style={[
                  styles.tableRow, 
                  index % 2 === 1 && { backgroundColor: '#f9fafb' }
                ]}
              >
                <TouchableOpacity 
                  style={[styles.rowCell, { width: 140 }]}
                  onPress={() => navigation.navigate('M07ResultScreen', { illustrationId: item.id })}
                >
                  <Text style={styles.customerName}>{item.name}</Text>
                </TouchableOpacity>

                <View style={[styles.rowCell, { width: 280 }]}>
                  <Text style={styles.proposalName}>{item.proposalName}</Text>
                </View>

                <View style={[styles.rowCell, { width: 100 }]}>
                  <Text style={styles.premiumText}>{item.premium}</Text>
                </View>

                <View style={[styles.rowCell, { width: 120 }]}>
                  <Text style={styles.dateText}>{item.lastUpdated}</Text>
                </View>

                <View style={[styles.rowCell, { width: 130 }]}>
                  <Text style={styles.statusText}>{item.status}</Text>
                </View>

                <TouchableOpacity 
                  style={[styles.rowCell, { width: 60, alignItems: 'center', justifyContent: 'center' }]}
                  onPress={() => handleDownloadPDF(item.name)}
                >
                  <Icon name="picture_as_pdf" size={22} color="#EF4444" />
                </TouchableOpacity>
              </View>
            ))}

            {filteredHistory.length === 0 && (
              <View style={styles.emptyContainer}>
                <Icon name="history" size={48} color={colors.onSurfaceVariant} />
                <Text style={styles.emptyText}>Chưa có lịch sử minh họa nào khớp</Text>
              </View>
            )}
          </View>
        </ScrollView>

        {/* Footer Notes */}
        <View style={styles.footerNote}>
          <Text style={styles.footerNoteText}>Đơn vị: Ngàn Đồng</Text>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  headerPanel: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing[4],
    backgroundColor: '#ffffff',
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.outlineVariant,
    gap: spacing[2],
  },
  searchWrapper: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.outline,
    borderRadius: borderRadius.DEFAULT,
    paddingHorizontal: spacing[3],
    backgroundColor: colors.surface,
    height: 40,
  },
  searchIcon: {
    marginRight: spacing[2],
  },
  searchInput: {
    flex: 1,
    height: '100%',
    color: colors.onSurface,
    fontSize: 13,
  },
  createBtn: {
    backgroundColor: '#EF4444',
    paddingHorizontal: spacing[4],
    height: 40,
    borderRadius: borderRadius.DEFAULT,
    alignItems: 'center',
    justifyContent: 'center',
  },
  createBtnText: {
    color: '#ffffff',
    fontWeight: fontWeights.bold,
    fontSize: 13,
  },
  scrollContent: {
    padding: spacing[4],
    paddingBottom: 88,
  },
  alertBanner: {
    flexDirection: 'row',
    backgroundColor: '#FFFDF5',
    borderWidth: 1,
    borderColor: '#FEF3C7',
    borderRadius: borderRadius.DEFAULT,
    padding: spacing[3],
    marginBottom: spacing[4],
    gap: spacing[2],
  },
  alertIcon: {
    marginTop: 2,
  },
  alertText: {
    flex: 1,
    fontSize: 11,
    lineHeight: 16,
    color: '#B45309',
    textAlign: 'justify',
  },
  scrollHelperText: {
    fontSize: 11,
    color: colors.onSurfaceVariant,
    marginBottom: spacing[2],
    fontStyle: 'italic',
  },
  tableScroll: {
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    borderRadius: borderRadius.md,
    backgroundColor: '#ffffff',
  },
  table: {
    minWidth: 830,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f9fafb',
    borderBottomWidth: 1,
    borderBottomColor: colors.outlineVariant,
    paddingVertical: spacing[3],
  },
  headerCell: {
    fontSize: 12,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
    paddingHorizontal: spacing[3],
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
    paddingVertical: spacing[4],
  },
  rowCell: {
    paddingHorizontal: spacing[3],
    justifyContent: 'center',
  },
  customerName: {
    fontSize: 13,
    fontWeight: fontWeights.semibold,
    color: colors.primary,
  },
  proposalName: {
    fontSize: 13,
    color: colors.onSurface,
  },
  premiumText: {
    fontSize: 13,
    fontWeight: '500',
    color: colors.onSurface,
  },
  dateText: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
  },
  statusText: {
    fontSize: 12,
    color: '#374151',
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing[10],
    gap: spacing[3],
  },
  emptyText: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
  },
  footerNote: {
    marginTop: spacing[3],
  },
  footerNoteText: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
    fontWeight: '500',
  },
});
